/**
 * ReturnGreeting — тёплое welcome'е после > 12h отсутствия.
 *
 * JB doc 26 audit §1 + §6: hook map гэп «Дни 1-3 — первый возврат» и
 * cozy principle #3 «daily rhythm встроенный в реальный» были не
 * реализованы. Раньше игрок возвращался — игра молчала. Cozy игры
 * (Animal Crossing, Spiritfarer) делают акцент на возврате: «о, ты
 * вернулся! я тебя ждал».
 *
 * Логика:
 *  - mount AppRoot → читаем useReturnVisitStore.lastVisitAt
 *  - null → первый заход, просто mark, ничего не показываем
 *  - hours < 12 → обычная сессия, mark, ничего
 *  - hours ≥ 12 → toast «вернулся. Роща тебя помнит.» + бонус в осознания
 *
 * Бонус — обычные осознания (через grantAtlasReward), пропорционально
 * длине отсутствия. См. returnBonusFor().
 *
 * Дизайн toast'а — тёплый gold backdrop card сверху, под AtlasToggle,
 * с DelayedButton 1s (anti-misclick, как у Sai-активаций).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import { computePassiveAwarenessPerSec } from '@sai-clicker/game-core';
import { useGameStore } from '../stores/game.js';
import {
  hoursSinceLastVisit,
  returnBonusFor,
  returnGreetingFor,
  useReturnVisitStore,
} from '../stores/returnVisit.js';
import { emit } from '../telemetry/emit.js';
import { DelayedButton } from './DelayedButton.js';
import styles from './ReturnGreeting.module.css';

/** Bucket hours для аналитики — без точного timing (privacy + grouping). */
function hoursBucket(hours: number): string {
  if (hours < 24) return '12-24h';
  if (hours < 72) return '1-3d';
  if (hours < 168) return '3-7d';
  return '7d+';
}

/** Cap offline accumulation — 8 hours. Дольше = same. */
const OFFLINE_ACCUMULATION_CAP_HOURS = 8;

interface GreetingPayload {
  readonly hours: number;
  readonly bonus: number;
  readonly offlineBonus: number;
  readonly text: string;
}

export function ReturnGreeting(): JSX.Element {
  const markVisit = useReturnVisitStore((s) => s.markVisit);
  const grantAtlasReward = useGameStore((s) => s.grantAtlasReward);
  // Доступ к покупкам — нужен чтобы посчитать пассивный доход за время offline.
  // Mechanic D doc 02: пассивный доход начисляется через offline-копилку при
  // возврате, capped at 8 hours. Доход в реальном времени не идёт — это
  // экономит battery и держит «выйди-вернись» рутину тёплой.
  const purchasedUpgrades = useGameStore((s) => s.state.purchasedUpgrades);
  const [pending, setPending] = useState<GreetingPayload | null>(null);
  // UX analytics — duration измеряется от показа до dismiss.
  const shownAtRef = useRef<number | null>(null);

  // На mount компонента: проверить сколько времени прошло.
  useEffect(() => {
    const hours = hoursSinceLastVisit();
    // Always mark current visit immediately — даже если первый заход
    // или короткая сессия. Это reset таймера для следующего раза.
    markVisit();

    if (hours === null) return; // Первый заход — без greeting.
    if (hours < 12) return; // Обычная сессия — без greeting.

    const bonus = returnBonusFor(hours);
    const text = returnGreetingFor(hours);

    // Mechanic D — offline accumulation (doc 02 §VII.D).
    // Пассивные апгрейды (Хранитель тишины, Утренняя роса и т.д.)
    // продолжают давать осознания пока игрок не в игре. Cap = 8 hours —
    // чтобы недельное отсутствие не давало взрывной бонус, а возврат
    // был ровно настолько ценным, чтобы быть приятным сюрпризом,
    // не game-changer'ом.
    const passivePerSec = computePassiveAwarenessPerSec(purchasedUpgrades, UPGRADES_ALPHA);
    const cappedSeconds = Math.min(hours * 3600, OFFLINE_ACCUMULATION_CAP_HOURS * 3600);
    const offlineBonus = Math.floor(passivePerSec * cappedSeconds);

    // Грантим суммарный бонус сразу — игрок видит +N в счётчике одновременно
    // с появлением toast'а. Это даёт «двойной» feedback (visual + numeric).
    grantAtlasReward(bonus + offlineBonus, 0);
    setPending({ hours, bonus, offlineBonus, text });
    shownAtRef.current = Date.now();
    emit('return_greeting.shown', {
      hours_bucket: hoursBucket(hours),
      bonus,
      offline_bonus: offlineBonus,
    });
  }, [markVisit, grantAtlasReward, purchasedUpgrades]);

  function handleDismiss(): void {
    if (shownAtRef.current !== null) {
      emit('return_greeting.dismissed', {
        duration_ms: Date.now() - shownAtRef.current,
      });
      shownAtRef.current = null;
    }
    setPending(null);
  }

  return (
    <AnimatePresence>
      {pending !== null && (
        <div className={styles.wrap}>
          <motion.div
            className={styles.card}
            initial={{ opacity: 0, y: -16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.25, 0.8, 0.3, 1] }}
          >
            <p className={styles.preface}>память Рощи</p>
            <p className={styles.greeting}>{pending.text}</p>
            <p className={styles.bonus}>
              память благодарит · +{pending.bonus.toString()} осознаний
            </p>
            {pending.offlineBonus > 0 && (
              <p className={styles.offlineBonus}>
                роща дышала с тобой · +{pending.offlineBonus.toString()} осознаний
              </p>
            )}
            <DelayedButton onClick={handleDismiss} delayMs={800} ariaLabel="Продолжить">
              хорошо
            </DelayedButton>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
